/* eslint-disable import/no-cycle */
import * as Yup from 'yup'

import { CAPITAL_FEDERAL_PROVINCE } from 'constants/corporateAgreements'
// eslint-disable-next-line import/no-cycle
import { isEmpty } from 'helpers/utils'
import {
  addressStreet,
  addressZipcode,
  alphanumericOrEmpty,
  fullAlphabetic,
  email,
  onlyNumbers,
  fullAlphabeticWithNumbers,
  currencyAmountDotSeparator,
  cuit
} from 'helpers/validations'
import { LANGUAGE_TAGS } from 'localization/constants/languages'
const getContactFields = translations =>
  Yup.object().shape({
    contactName: Yup.string()
      .test(
        'contactName',
        translations('agreements.request.contact-name.pattern'),
        value => isEmpty(value) || new RegExp(fullAlphabetic).test(value)
      )
      .required(translations('agreements.request.contact-name.required')),
    contactLastname: Yup.string()
      .test(
        'contactLastname',
        translations('agreements.request.contact-lastname.pattern'),
        value => isEmpty(value) || new RegExp(fullAlphabetic).test(value)
      )
      .required(translations('agreements.request.contact-lastname.required')),
    jobTitle: Yup.string()
      .test(
        'contactJobTitle',
        translations('agreements.request.contact-job-title.pattern'),
        value => isEmpty(value) || new RegExp(fullAlphabetic).test(value)
      )
      .required(translations('agreements.request.contact-job-title.required')),
    email: Yup.string()
      .test(
        'contactEmail',
        translations('general.validation.email.pattern'),
        value => isEmpty(value) || new RegExp(email).test(value)
      )
      .required(translations('general.validation.email.required')),
    phoneNumber: Yup.string()
      .test(
        'contactPhone',
        translations('general.validation.phone-number.pattern'),
        value => isEmpty(value) || new RegExp(onlyNumbers).test(value)
      )
      .required(translations('general.validation.phone-number.required')),
    directTravelsManagement: Yup.bool(),
    travelAgencyName: Yup.string().when('directTravelsManagement', (value, schema) =>
      !value
        ? Yup.string().required(
            translations('agreements.request.contact-direct-travel-agency.name.required')
          )
        : schema
    )
  })

const getEnterpriseFields = translations =>
  Yup.object().shape({
    enterpriseName: Yup.string()
      .test(
        'enterpriseName',
        translations('agreements.request.enterprise-name.pattern'),
        value => isEmpty(value) || new RegExp(fullAlphabeticWithNumbers).test(value)
      )
      .required(translations('agreements.request.enterprise-name.required')),
    enterpriseCuit: Yup.string()
      .required(translations('agreements.request.enterprise-cuit.required'))
      .test('enterpriseCuit', translations('agreements.request.enterprise-cuit.pattern'), value =>
        new RegExp(cuit).test(value)
      ),
    enterpriseActivity: Yup.string()
      .test(
        'enterpriseActivity',
        translations('agreements.request.enterprise-activity.pattern'),
        value => isEmpty(value) || new RegExp(fullAlphabetic).test(value)
      )
      .required(translations('agreements.request.enterprise-activity.required')),
    enterpriseUrl: Yup.string().required(
      translations('agreements.request.enterprise-web.required')
    ),
    enterpriseAddress: Yup.object().shape({
      country: Yup.mixed()
        .required(translations('general.validation.country-code.required'))
        .nullable(),
      state: Yup.mixed()
        .when(['country', '$provinces'], {
          is: (country, provinces) =>
            country && country.value == LANGUAGE_TAGS.ONLY_ARG_LANG && !isEmpty(provinces),
          then: () =>
            Yup.string().required(translations('general.validation.address-province.required')),
          otherwise: schema => schema
        })
        .nullable(),
      stateInput: Yup.mixed().nullable(),
      city: Yup.string().when('state', {
        is: provinces => provinces?.label !== CAPITAL_FEDERAL_PROVINCE,
        then: () =>
          Yup.string()
            .test(
              'enterpriseCity',
              translations('agreements.request.enterprise-city.pattern'),
              value => isEmpty(value) || new RegExp(fullAlphabetic).test(value)
            )
            .required(translations('agreements.request.enterprise-city.required'))
      }),
      street: Yup.string()
        .test(
          'enterpriseStreet',
          translations('agreements.request.enterprise-street.pattern'),
          value => isEmpty(value) || new RegExp(addressStreet).test(value)
        )
        .required(translations('agreements.request.enterprise-street.required')),
      number: Yup.string()
        .test(
          'enterpriseNumber',
          translations('agreements.request.enterprise-number.pattern'),
          value => isEmpty(value) || new RegExp(onlyNumbers).test(value)
        )
        .required(translations('agreements.request.enterprise-number.required')),
      floor: Yup.string().test(
        'enterpriseFloor',
        translations('agreements.request.enterprise-floor.pattern'),
        value => new RegExp(onlyNumbers).test(value)
      ),
      apartment: Yup.string().test(
        'enterpriseApartment',
        translations('agreements.request.enterprise-apartment.pattern'),
        value => new RegExp(alphanumericOrEmpty).test(value)
      ),
      zipCode: Yup.string()
        .test(
          'enterpriseZipCode',
          translations('agreements.request.enterprise-zip-code.pattern'),
          value => new RegExp(addressZipcode).test(value)
        )
        .required(translations('agreements.request.enterprise-zip-code.required'))
    })
  })

const getSpendingTest = (ValidationSchema, translations) =>
  ValidationSchema.test(
    'anualSpending',
    translations('agreements.request.travels.anual-spending.pattern'),
    value => isEmpty(value) || new RegExp(currencyAmountDotSeparator).test(value)
  )

const getRouteSchema = translations =>
  Yup.object().shape(
    {
      origin: Yup.mixed()
        .nullable()
        .when(['destination', 'anualSpending'], {
          is: (destination, anualSpending) => destination || anualSpending,
          then: () =>
            Yup.string().required(translations('agreements.request.travels.origin.required')),
          otherwise: schema => schema
        }),
      destination: Yup.mixed()
        .nullable()
        .when(['origin', 'anualSpending'], {
          is: (origin, anualSpending) => origin || anualSpending,
          then: () =>
            Yup.string().required(translations('agreements.request.travels.destination.required')),
          otherwise: schema => schema
        }),
      anualSpending: getSpendingTest(
        Yup.string().when(['origin', 'destination'], {
          is: (origin, destination) => origin || destination,
          then: () =>
            Yup.string().required(
              translations('agreements.request.travels.anual-spending.required')
            ),
          otherwise: schema => schema
        }),
        translations
      )
    },
    [
      ['origin', 'destination'],
      ['origin', 'anualSpending'],
      ['destination', 'anualSpending']
    ] //Needed due to cyclic dependency issue
  )

const getRequiredRouteSchema = translations =>
  Yup.object().shape({
    origin: Yup.mixed()
      .required(translations('agreements.request.travels.origin.required'))
      .nullable(),
    destination: Yup.mixed()
      .required(translations('agreements.request.travels.destination.required'))
      .nullable(),
    anualSpending: getSpendingTest(Yup.string()).required(
      translations('agreements.request.travels.anual-spending.required')
    )
  })

const getTravelSchema = (fields, translations) => {
  const schema = {}
  Object.entries(fields).forEach(([market, routesObject]) => {
    const marketSchema = {}
    Object.entries(routesObject).forEach((_, index) => {
      if (index) {
        marketSchema[index] = getRequiredRouteSchema(translations)
      } else {
        marketSchema[index] = getRouteSchema(translations)
      }
    })
    schema[market] = Yup.object().shape(marketSchema)
  })
  return Yup.object().shape(schema)
}

export const getAgreementRequestValidationSchema = (values, translations) =>
  Yup.object().shape({
    enterpriseFields: getEnterpriseFields(translations),
    contactFields: getContactFields(translations),
    travelsFields: getTravelSchema(values.travelsFields, translations),
    contactAcceptTermsConditions: Yup.bool().test(
      'contactAcceptTermsConditions',
      translations('agreements.request.contact-agreement'),
      value => value
    )
  })
